import React, { useState, useRef, memo, forwardRef, useImperativeHandle } from 'react';
import { oneOf, string, bool, number } from 'prop-types';
import classnames from 'classnames';
import shortid from 'shortid';

import { InputBase, IconButton, Menu } from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';

import { Label } from 'common/ThemedTextField';
import ThemedMenuItem from 'common/ThemedMenuItem';
import CheckIcon from 'images/icons/check.svg';

import useStyles from './useStyles';

const NestedSelect = forwardRef(
  (
    {
      shrink = false,
      label = '',
      disabled = false,
      outline = false,
      variant = 'TextField',
      invalid = false,
      keepFilled = false,
      keepFocused = false,
      selectedIcon = <CheckIcon fill="orange" />,
      options,
      onChange,
      value = '',
      name,
      className = '',
      onFocus,
      onBlur,
      inputProps,
      type = 'text',
      startAdornment,
      multiline = false,
      style,
      rows,
    },
    ref
  ) => {
    const inputContainerRef = useRef(null);
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const [opened, setOpened] = useState(false);
    const [focused, setFocused] = useState(false || keepFocused);
    const themed = useStyles();
    const classes = classnames('ThemedTextField', className, themed.TextField, {
      [themed.TextField_Focused]: (focused && !disabled) || opened || keepFocused,
      [themed.TextField_Empty]: !focused && value === '' && outline,
      [themed.TextField_Filled]: (!focused && value !== '') || !outline || shrink,
      [themed.TextField_Disabled]: disabled,
      [themed.TextField_Invalid]: invalid,
      [themed.TextField_Outline]: outline,
      [themed.TextField_Multiline]: multiline,
    });

    const handleChange = (option, key) => (e) => {
      if (onChange && option && !disabled) {
        onChange({ target: { ...e, ...option, optionKey: key, name } });
      }
    };

    const handleCloseMenu = () => {
      setOpened(false);
    };

    return (
      <div className={classes} style={style}>
        <div
          className="ThemedTextField__container"
          style={{ position: 'relative', display: 'flex', flexWrap: 'nowrap' }}
        >
          <Label
            active={focused || opened}
            filled={
              (focused && !disabled) ||
              value !== '' ||
              (opened && !disabled) ||
              shrink ||
              keepFilled
            }
            disabled={disabled}
            invalid={invalid}
            multiline={multiline}
          >
            {label}
          </Label>

          {variant === 'Dropdown' && (
            <Menu
              classes={{ paper: themed.Dropdown__Menu }}
              style={{ marginLeft: 13 }}
              MenuListProps={{
                style: { width: inputContainerRef.current?.parentElement.offsetWidth },
              }}
              anchorEl={inputContainerRef.current}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              open={opened && !disabled}
              onClose={(e, reason) => {
                if (reason === 'backdropClick') {
                  setOpened((o) => !o);
                  setFocused(false);
                } else {
                  handleCloseMenu();
                }
              }}
              // onBackdropClick={() => {}}
              // onClose={handleCloseMenu}
            >
              {Object.keys(options).map((key, keyIdx) =>
                options[key].map((option, optionIdx) => (
                  <ThemedMenuItem
                    key={shortid()}
                    selected={option.selected}
                    onClick={handleChange(option, key)}
                    leftIcon={option.selected ? selectedIcon : <CheckIcon fill="transparent" />}
                    selectedClassName={themed.Dropdown__selected}
                    rootClassName={`${themed.Dropdown__item} ${
                      keyIdx > 0 && optionIdx === 0 ? themed.Dropdown__divider : ''
                    }`}
                  >
                    {option.text}
                  </ThemedMenuItem>
                ))
              )}
            </Menu>
          )}
          <InputBase
            rows={rows}
            multiline={multiline}
            startAdornment={startAdornment}
            ref={inputContainerRef}
            inputRef={inputRef}
            disabled={disabled}
            inputProps={inputProps}
            onFocus={(e) => {
              if (disabled) return;
              if (typeof onFocus === 'function') onFocus(e);
              if (variant === 'Dropdown') return;
              setFocused(true);
            }}
            onClick={() => {
              if (disabled) return;
              if (variant !== 'Dropdown') return;
              setOpened((open) => !open);
              setFocused(true);
            }}
            onBlur={(e) => {
              if (disabled) return;
              if (typeof onBlur === 'function') onBlur(e);
              setFocused(false);
            }}
            type={type}
            className={themed.TextField__input}
            onChange={handleChange()}
            value={value}
          />

          {variant === 'Dropdown' && !disabled && (
            <IconButton
              size="small"
              className={themed.TextField__icon_button}
              onClick={() => {
                setOpened(!opened);
              }}
            >
              {opened ? (
                <ExpandLess className={themed.TextField__icon} />
              ) : (
                <ExpandMore className={themed.TextField__icon} />
              )}
            </IconButton>
          )}
        </div>
      </div>
    );
  }
);

NestedSelect.propTypes = {
  multiline: bool,
  label: string,
  // icon: element,
  disabled: bool,
  variant: oneOf(['TextField', 'Dropdown', 'Autocomplete']),
  invalid: bool,
  rows: number,
};

export default memo(NestedSelect);
